import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { InvoiceStatus } from "@api/shared-runtime/enums";
import { Skeleton } from "@/components/ui/skeleton";
import { Permission, usePermissions } from "@/hooks/usePermissions";
import { trpc } from "@/trpc";
import { UsageBillingInvoices } from "./usage-billing-invoices";

export function UsageBillingInvoicesPanel({
  organizationBusinessUnitId,
  unitName,
}: {
  organizationBusinessUnitId: string;
  unitName: string;
}) {
  const queryClient = useQueryClient();
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);
  const queryOptions = trpc.adminInvoices.getUnitInvoices.queryOptions({
    organizationBusinessUnitId,
  });
  const invoicesQuery = useQuery(queryOptions);
  const userQuery = useQuery(trpc.user.getUser.queryOptions());
  const { hasPermission } = usePermissions(userQuery.data);
  const canCharge = hasPermission(Permission.AppChargeOrganizationInvoices);
  const canManageInvoices = userQuery.data?.is_admin === true;

  async function refresh() {
    await queryClient.invalidateQueries(queryOptions);
  }

  // Cached data can lag behind a contact or card the unit saved in another
  // tab, so a blocked action is only reported after a fresh read says so.
  function loadFreshData() {
    return queryClient.fetchQuery({ ...queryOptions, staleTime: 0 });
  }

  const generateMutation = useMutation(
    trpc.adminInvoices.generateDrafts.mutationOptions({
      onSuccess: async (result) => {
        if (result.failed.length > 0) {
          toast.error(`${result.failed.length} cycle(s) could not be drafted`, {
            description: result.failed[0].reason,
          });
        } else {
          toast.success(`${result.generated.length} draft(s) generated`);
        }
        await refresh();
      },
      onError: (error) => {
        toast.error("Couldn't generate drafts", { description: error.message });
      },
    }),
  );

  const issueMutation = useMutation(
    trpc.adminInvoices.issueInvoice.mutationOptions({
      onSuccess: async () => {
        toast.success("Invoice issued");
        await refresh();
      },
      onError: async (error) => {
        toast.error("Could not issue invoice", { description: error.message });
        await refresh();
      },
      onSettled: () => setBusyInvoiceId(null),
    }),
  );

  const emailMutation = useMutation(
    trpc.adminInvoices.emailInvoice.mutationOptions({
      onSuccess: async () => {
        toast.success("Invoice emailed");
        await refresh();
      },
      onError: async (error) => {
        toast.error("Could not email the invoice", {
          description: error.message,
        });
        await refresh();
      },
      onSettled: () => setBusyInvoiceId(null),
    }),
  );

  const discardMutation = useMutation(
    trpc.adminInvoices.discardDraft.mutationOptions({
      onSuccess: async () => {
        toast.success("Draft discarded");
        await refresh();
      },
      onError: (error) => {
        toast.error("Couldn't discard the draft", {
          description: error.message,
        });
      },
      onSettled: () => setBusyInvoiceId(null),
    }),
  );

  const voidMutation = useMutation(
    trpc.adminInvoices.voidInvoice.mutationOptions({
      onSuccess: async () => {
        toast.success("Invoice voided");
        await refresh();
      },
      onError: async (error) => {
        toast.error("Couldn't void the invoice", {
          description: error.message,
        });
        await refresh();
      },
      onSettled: () => setBusyInvoiceId(null),
    }),
  );

  function describeChargeResult(result: { status: InvoiceStatus }): string {
    return result.status === InvoiceStatus.Paid
      ? "Invoice paid"
      : "Charge submitted, awaiting payment";
  }

  const chargeDirectMutation = useMutation(
    trpc.adminInvoices.chargeDirectInvoice.mutationOptions({
      onSuccess: async (result) => {
        toast.success(describeChargeResult(result));
        await refresh();
      },
      onError: async (error) => {
        toast.error("Charge failed", { description: error.message });
        await refresh();
      },
      onSettled: () => setBusyInvoiceId(null),
    }),
  );

  const chargeMutation = useMutation(
    trpc.adminInvoices.chargeInvoice.mutationOptions({
      onSuccess: async (result) => {
        toast.success(describeChargeResult(result));
        await refresh();
      },
      onError: async (error) => {
        toast.error("Charge failed", { description: error.message });
        await refresh();
      },
      onSettled: () => setBusyInvoiceId(null),
    }),
  );

  const resendMutation = useMutation(
    trpc.adminInvoices.sendInvoice.mutationOptions({
      onSuccess: () => {
        toast.success("Invoice emailed again");
      },
      onError: (error) => {
        toast.error("Couldn't email the invoice", {
          description: error.message,
        });
      },
      onSettled: () => setBusyInvoiceId(null),
    }),
  );

  if (!invoicesQuery.data) {
    return (
      <div className="p-4" role="status">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <UsageBillingInvoices
      data={invoicesQuery.data}
      unitName={unitName}
      canCharge={canCharge}
      canManageInvoices={canManageInvoices}
      isGenerating={generateMutation.isPending}
      busyInvoiceId={busyInvoiceId}
      onGenerate={() => generateMutation.mutate({ organizationBusinessUnitId })}
      loadFreshData={loadFreshData}
      onIssue={(invoiceId) => {
        setBusyInvoiceId(invoiceId);
        issueMutation.mutate({ invoiceId });
      }}
      onDiscard={(invoiceId) => {
        setBusyInvoiceId(invoiceId);
        discardMutation.mutate({ invoiceId });
      }}
      onCharge={({ invoiceId, expectedVersion }) => {
        setBusyInvoiceId(invoiceId);
        chargeMutation.mutate({ invoiceId, expectedVersion });
      }}
      onChargeDirect={({ invoiceId, expectedVersion }) => {
        setBusyInvoiceId(invoiceId);
        chargeDirectMutation.mutate({ invoiceId, expectedVersion });
      }}
      onEmail={(invoiceId) => {
        setBusyInvoiceId(invoiceId);
        emailMutation.mutate({ invoiceId });
      }}
      onResend={(invoiceId) => {
        setBusyInvoiceId(invoiceId);
        resendMutation.mutate({ invoiceId });
      }}
      onVoid={(invoiceId) => {
        setBusyInvoiceId(invoiceId);
        voidMutation.mutate({ invoiceId });
      }}
    />
  );
}
